/**
 * WorkerHealthBanner
 *
 * Polls GET /health/worker every 15 seconds and shows a warning stripe
 * when the worker process is stale or offline. Only visible when worker
 * status is not "healthy".
 *
 * When worker_state is "BUSY" and heartbeat is fresh, shows an
 * informational "busy" banner instead of stale/offline.
 */

import { useState, useEffect } from 'react'
import { AlertTriangle, Loader } from 'lucide-react'
import { getAPIBaseUrl } from '../../environment'

const API_BASE_URL = getAPIBaseUrl()
const POLL_INTERVAL_MS = 15_000

export default function WorkerHealthBanner() {
  const [health, setHealth] = useState(null)

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/health/worker`)
        if (response.ok) {
          const data = await response.json()
          setHealth(data)
        }
      } catch {
        // API unreachable — worker is likely also down
        setHealth({ status: 'offline', last_heartbeat: null, age_seconds: null })
      }
    }

    checkHealth()
    const interval = setInterval(checkHealth, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  if (!health || health.status === 'unknown') return null

  // Worker is BUSY executing a job — show informational banner (not an error)
  const isBusy = health.worker_state === 'BUSY' && health.status === 'healthy'
  if (isBusy) {
    return (
      <div
        style={{
          background: '#1a3a5c',
          color: '#fff',
          padding: '5px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
          letterSpacing: '0.01em',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          flexShrink: 0,
        }}
      >
        <Loader size={13} style={{ flexShrink: 0, animation: 'spin 2s linear infinite' }} />
        <span>Worker busy — executing a job. New jobs will queue until complete.</span>
      </div>
    )
  }

  // Healthy + idle = no banner
  if (health.status === 'healthy') return null

  const isStale = health.status === 'stale'

  let msg
  if (isStale) {
    msg = `Worker heartbeat is stale (last seen ${health.age_seconds}s ago) — jobs may be slow to start.`
  } else if (health.last_heartbeat) {
    msg = `Worker offline (last seen ${health.age_seconds}s ago) — queued jobs will not execute.`
  } else {
    msg = `Worker is not running — queued jobs will not execute.`
  }

  return (
    <div
      style={{
        background: isStale ? '#7c5500' : '#7c1500',
        color: '#fff',
        padding: '5px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        letterSpacing: '0.01em',
        borderBottom: '1px solid rgba(255,255,255,0.12)',
        flexShrink: 0,
      }}
    >
      <AlertTriangle size={13} style={{ flexShrink: 0 }} />
      <span>{msg}</span>
    </div>
  )
}
